import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight, Check } from 'lucide-react';
import { playClickSoft, playReveal } from '../utils/sound';

const API = `${window.location.origin}/api`;

/* ──────────────────────────────────────────
 * #18 — Scent Personality Quiz
 * 5 questions → matches a fragrance via /api/products + simple weighted scoring.
 * No backend needed — pulls products and computes locally so it works offline.
 * ────────────────────────────────────────── */

const QUESTIONS = [
  {
    id: 'mood',
    question: 'When you walk into a room, you want to feel…',
    options: [
      { label: 'Calm and grounded', tags: ['woody','musk'] },
      { label: 'Bold and unforgettable', tags: ['oud','spicy'] },
      { label: 'Warm and addictive', tags: ['gourmand','sweet'] },
      { label: 'Fresh and effortless', tags: ['fresh','citrus','aquatic'] },
    ],
  },
  {
    id: 'occasion',
    question: 'Where will you wear it most?',
    options: [
      { label: 'Date nights', tags: ['oud','sweet','gourmand'] },
      { label: 'Office / daytime', tags: ['fresh','citrus','musk'] },
      { label: 'Weekend chill', tags: ['woody','musk'] },
      { label: 'Big events', tags: ['oud','spicy','floral'] },
    ],
  },
  {
    id: 'season',
    question: 'Your favorite weather is…',
    options: [
      { label: 'Sunny & warm', tags: ['citrus','fresh','floral'] },
      { label: 'Cool & rainy', tags: ['woody','oud','sweet'] },
      { label: 'Cold nights', tags: ['oud','sweet','spicy','gourmand'] },
      { label: 'Sea breeze', tags: ['aquatic','fresh','citrus'] },
    ],
  },
  {
    id: 'vibe',
    question: 'Pick a vibe that describes you',
    options: [
      { label: 'Quiet luxury', tags: ['musk','woody'] },
      { label: 'Magnetic & sensual', tags: ['oud','gourmand','sweet'] },
      { label: 'Romantic dreamer', tags: ['floral','sweet'] },
      { label: 'Modern minimalist', tags: ['fresh','citrus','musk'] },
    ],
  },
  {
    id: 'budget',
    question: 'Comfort budget per bottle?',
    options: [
      { label: 'Up to ₱1,000', max: 1000 },
      { label: '₱1,000 – ₱2,000', max: 2000 },
      { label: '₱2,000 – ₱3,500', max: 3500 },
      { label: 'Splurge mode', max: 99999 },
    ],
  },
];

function scoreProduct(product, tagBag, budget) {
  const blob = [
    product.scent_family, product.family,
    ...(Array.isArray(product.notes) ? product.notes : []),
    ...(product.notes?.top || []),
    ...(product.notes?.middle || product.notes?.heart || []),
    ...(product.notes?.base || []),
    ...(product.top_notes || []),
    ...(product.middle_notes || product.heart_notes || []),
    ...(product.base_notes || []),
    product.description, product.vibe, product.tagline,
  ].filter(Boolean).join(' ').toLowerCase();

  let score = 0;
  for (const [tag, weight] of Object.entries(tagBag)) {
    if (blob.includes(tag)) score += weight * 10;
  }
  // Light boost for signature/in-stock
  if (product.is_signature) score += 5;
  // Budget penalty if over
  const price = product.base_price || 0;
  if (budget && price > budget) score -= 15;
  return score;
}

export default function ScentQuizPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [matches, setMatches] = useState(null);
  const [loading, setLoading] = useState(false);

  const total = QUESTIONS.length;
  const q = QUESTIONS[step];
  const progress = ((step) / total) * 100;

  const choose = (opt) => {
    playClickSoft();
    const next = { ...answers, [q.id]: opt };
    setAnswers(next);
    if (step < total - 1) {
      setTimeout(() => setStep(step + 1), 150);
    } else {
      finish(next);
    }
  };

  const finish = async (allAnswers) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/products`);
      const products = r.data || [];

      const tagBag = {};
      let budget = 99999;
      for (const a of Object.values(allAnswers)) {
        for (const t of (a.tags || [])) tagBag[t] = (tagBag[t] || 0) + 1;
        if (typeof a.max === 'number') budget = a.max;
      }

      const scored = products
        .filter(p => (p.stock || (p.variants?.reduce((s, v) => s + (v.stock || 0), 0)) || 0) > 0)
        .map(p => ({ ...p, _score: scoreProduct(p, tagBag, budget) }))
        .sort((a, b) => b._score - a._score)
        .slice(0, 3);

      setMatches(scored);
      setTimeout(() => playReveal(), 200);
    } finally { setLoading(false); }
  };

  const restart = () => { setAnswers({}); setMatches(null); setStep(0); };

  return (
    <div data-testid="scent-quiz-page" className="min-h-screen bg-[#0A0A0A] py-16 sm:py-24">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        {!matches ? (
          <>
            {/* Progress */}
            <div className="mb-10">
              <div className="flex items-center justify-between mb-3 text-[10px] uppercase tracking-[0.25em] text-white/40 font-sans">
                <span>The Scent Quiz</span>
                <span>{step + 1} / {total}</span>
              </div>
              <div className="h-[2px] bg-white/5 overflow-hidden">
                <div className="h-full bg-[#D4AF37] transition-all duration-500" style={{ width: `${progress + (100 / total)}%` }} />
              </div>
            </div>

            {/* Question */}
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3 font-sans flex items-center gap-2">
              <Sparkles className="w-3 h-3" /> Question {step + 1}
            </p>
            <h1 className="hero-headline text-3xl sm:text-4xl tracking-tight font-light text-white mb-8 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              {q.question}
            </h1>

            <div className="grid sm:grid-cols-2 gap-3">
              {q.options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => choose(opt)}
                  data-testid={`quiz-option-${q.id}-${i}`}
                  className="text-left p-5 bg-[#141414] border border-white/8 hover:border-[#D4AF37] hover:bg-[#D4AF37]/5 transition-all group"
                >
                  <p className="text-white text-sm font-sans group-hover:text-[#D4AF37] transition-colors">{opt.label}</p>
                </button>
              ))}
            </div>

            {step > 0 && (
              <button
                onClick={() => setStep(step - 1)}
                className="mt-8 text-white/30 hover:text-white/70 text-xs uppercase tracking-[0.15em] font-sans"
                data-testid="quiz-back-btn"
              >
                ← Back
              </button>
            )}
          </>
        ) : (
          <div data-testid="quiz-results">
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37] mb-3 font-sans flex items-center gap-2">
              <Check className="w-3 h-3" /> Your matches
            </p>
            <h1 className="hero-headline text-3xl sm:text-5xl tracking-tight font-light text-white mb-3 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              These were made for you.
            </h1>
            <p className="text-white/60 text-sm font-sans mb-10">Based on your mood, occasion, and vibe — here are 3 fragrances we think you'll love.</p>

            {loading ? (
              <p className="text-white/40 text-sm">Finding your scents…</p>
            ) : (
              <div className="space-y-3">
                {matches.map((p, idx) => (
                  <div
                    key={p.id}
                    data-testid={`quiz-match-${idx}`}
                    onClick={() => navigate(`/product/${p.id}`)}
                    className="flex items-center gap-4 p-4 bg-[#141414] border border-white/8 hover:border-[#D4AF37]/40 transition-colors cursor-pointer group"
                  >
                    <span className={`w-7 h-7 flex items-center justify-center text-xs font-sans ${idx === 0 ? 'bg-[#D4AF37] text-black font-medium' : 'bg-white/5 text-white/60'}`}>
                      {idx + 1}
                    </span>
                    <img src={p.image_url} alt="" className="w-14 h-16 object-cover bg-black flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-light text-lg group-hover:text-[#D4AF37] transition-colors" style={{ fontFamily: 'Cormorant Garamond, serif' }}>{p.name}</p>
                      <p className="text-[#D4AF37] text-sm font-sans font-medium">₱{p.base_price?.toLocaleString()}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-white/30 group-hover:text-[#D4AF37] group-hover:translate-x-1 transition-all" />
                  </div>
                ))}
                {matches.length === 0 && (
                  <p className="text-white/30 text-sm">No close matches yet — try our full collection.</p>
                )}
              </div>
            )}

            <div className="mt-10 flex flex-wrap gap-3">
              <button onClick={restart} data-testid="quiz-restart-btn"
                className="px-6 py-3 border border-white/15 text-white/70 hover:text-white hover:border-white/30 text-xs uppercase tracking-[0.2em] font-sans transition-colors">
                Retake quiz
              </button>
              <button onClick={() => navigate('/shop')}
                className="btn-shimmer px-6 py-3 bg-[#D4AF37] text-black text-xs uppercase tracking-[0.2em] font-medium font-sans hover:bg-[#E5C75A] transition-colors">
                Browse all fragrances →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
